import { z } from "zod";
import {
  mcpCredentialTransportSchema,
  mcpEndpointSchema,
  mcpHttpsUrlSchema,
  mcpIntegrationIdSchema,
  mcpToolNameSchema,
} from "./copilot-mcp.js";

/**
 * `COPILOT_MCP_SERVERS` — LEGACY IMPORT ONLY.
 *
 * This file parses the environment catalog that used to BE the catalog. It is
 * read by exactly one caller, `scripts/import-mcp-catalog.ts`, which an
 * operator runs once to move an existing deployment's integrations into the
 * database. Nothing in the running API imports it: `app.ts` builds the catalog
 * from PostgreSQL and from nothing else.
 *
 * That is the load-bearing sentence. The failure mode this file is shaped to
 * avoid is TWO ACTIVE CATALOGS — an env entry and a database row for the same
 * slug, disagreeing about an endpoint or an allowlist, with the answer
 * depending on which layer a given code path happened to consult. There is no
 * merge, no fallback, and no "env wins if the database is empty". An empty
 * database means no business tools, which is a state an administrator can see
 * and fix in the UI in under a minute.
 *
 * ── Why the shapes differ from the live ones ────────────────────────────────
 *
 * The environment catalog named its credentials by VARIABLE — `clientIdEnv`,
 * `clientSecretEnv`, `credentialEnv` — because there was no encrypted store to
 * put them in. The importer's job is precisely to resolve those names against
 * `process.env` once, at import time, and write the VALUES into
 * `copilot_mcp_integration_secrets` encrypted. So this file keeps the old
 * shapes verbatim, and they exist nowhere else.
 *
 * When the last deployment has run the import, delete this file, its script and
 * `COPILOT_MCP_SERVERS`. It is scaffolding with a known demolition date.
 */

/**
 * An env var NAME, never a value.
 *
 * Kept from the original schema for the reason it was written: it catches an
 * operator who pasted the secret itself into the field — the single most likely
 * way a credential ends up in a config file — and it means the string is safe
 * to print, because by construction it is a name.
 */
const envVarNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .regex(/^[A-Z][A-Z0-9_]*$/, {
    message:
      "Reference the credential by environment variable NAME (A-Z, 0-9, _) — never paste the secret itself.",
  });

function legacyAuthSchema(options: { allowInsecureLocalhost?: boolean }) {
  return z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("none") }).strict(),
    z
      .object({
        kind: z.literal("service-account"),
        shared: z.literal(true),
        transport: mcpCredentialTransportSchema.default({ kind: "bearer" }),
        credentialEnv: envVarNameSchema,
      })
      .strict(),
    z
      .object({
        kind: z.literal("token-user"),
        transport: mcpCredentialTransportSchema.default({ kind: "bearer" }),
        credentialLabel: z.string().trim().min(1).max(60).default("API token"),
        credentialHint: z.string().trim().max(300).nullish(),
      })
      .strict(),
    z
      .object({
        kind: z.literal("oauth2-user"),
        authorizationUrl: mcpHttpsUrlSchema({
          ...options,
          label: "OAuth authorization URL",
        }),
        tokenUrl: mcpHttpsUrlSchema({ ...options, label: "OAuth token URL" }),
        revocationUrl: mcpHttpsUrlSchema({
          ...options,
          label: "OAuth revocation URL",
        }).nullish(),
        clientIdEnv: envVarNameSchema,
        clientSecretEnv: envVarNameSchema.nullish(),
        scopes: z.array(z.string().trim().min(1).max(200)).max(40).default([]),
        pkce: z.boolean().default(true),
        audience: z.string().trim().max(200).nullish(),
        accountNameField: z.string().trim().max(120).nullish(),
        accountIdField: z.string().trim().max(120).nullish(),
        transport: mcpCredentialTransportSchema.default({ kind: "bearer" }),
      })
      .strict(),
  ]);
}

function legacyEntrySchema(options: { allowInsecureLocalhost?: boolean }) {
  return z
    .object({
      id: mcpIntegrationIdSchema,
      displayName: z.string().trim().min(1).max(80),
      description: z.string().trim().max(300).nullish(),
      icon: z
        .string()
        .trim()
        .max(60)
        .regex(/^[a-z0-9 _-]+$/i, { message: "Use a plain icon token." })
        .nullish(),
      endpoint: mcpEndpointSchema(options),
      enabled: z.boolean().default(true),
      auth: legacyAuthSchema(options).default({ kind: "none" }),
      timeoutMs: z.coerce.number().int().min(1_000).max(60_000).default(15_000),
      allowedTools: z.array(mcpToolNameSchema).min(1).max(50),
      readOnly: z.boolean().default(true),
    })
    .strict();
}

export type LegacyMcpServerEntry = z.infer<ReturnType<typeof legacyEntrySchema>>;

/**
 * The Phase 1 auth shapes, recognized ONLY in order to be refused with an
 * explanation.
 *
 * `{"kind":"bearer","tokenEnv":"X"}` used to mean "send the deployment's token
 * for every user", and nothing in the shape said so. Silently mapping it onto
 * `service-account` would import a shared credential by accident, which is
 * exactly the thing the per-user refactor existed to end.
 */
const LEGACY_AUTH_KINDS = new Set(["bearer", "header"]);

function legacyAuthProblem(entry: unknown, index: number): string | null {
  if (typeof entry !== "object" || entry === null) return null;
  const auth = (entry as { auth?: unknown }).auth;
  if (typeof auth !== "object" || auth === null) return null;
  const kind = (auth as { kind?: unknown }).kind;
  if (typeof kind !== "string" || !LEGACY_AUTH_KINDS.has(kind)) return null;
  const id = (entry as { id?: unknown }).id;
  const named = typeof id === "string" && id ? `"${id}"` : `entry ${index}`;
  return (
    `COPILOT_MCP_SERVERS: integration ${named} still uses the deployment-wide ` +
    `auth kind "${kind}", which no longer exists. Every user's Copilot call ` +
    `authenticated as the deployment under it. Re-create it in the Business ` +
    `Integrations admin screen as OAuth 2.0 or Personal API token so each user ` +
    `connects their own account, or — only if this credential really is shared ` +
    `and has no per-user identity — as a Shared service account.`
  );
}

/**
 * Parse `COPILOT_MCP_SERVERS` for the importer.
 *
 * Returns the entries or a list of human-readable problems — never throws and
 * never partially applies. A catalog that does not parse imports NOTHING, with
 * the reasons printed, because the alternative is an operator who wired three
 * integrations, got two, and finds out which one is missing from a support
 * ticket.
 */
export function parseLegacyMcpServerRegistry(
  raw: string,
  options: { allowInsecureLocalhost?: boolean } = {},
): { servers: LegacyMcpServerEntry[]; errors: string[] } {
  const text = raw.trim();
  if (!text) return { servers: [], errors: [] };

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return {
      servers: [],
      errors: [
        `COPILOT_MCP_SERVERS is not valid JSON: ${
          error instanceof Error ? error.message : "parse failed"
        }`,
      ],
    };
  }

  // A single object is accepted as a one-entry catalog: the overwhelmingly
  // common first configuration is one integration.
  const list = Array.isArray(parsed) ? parsed : [parsed];

  const legacy = list
    .map((entry, index) => legacyAuthProblem(entry, index))
    .filter((problem): problem is string => problem !== null);
  if (legacy.length > 0) return { servers: [], errors: legacy };

  const result = z
    .array(legacyEntrySchema(options))
    .max(50)
    .superRefine((entries, ctx) => {
      const seen = new Set<string>();
      for (const entry of entries) {
        if (seen.has(entry.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Duplicate MCP integration id "${entry.id}".`,
          });
        }
        seen.add(entry.id);
      }
    })
    .safeParse(list);

  if (!result.success) {
    return {
      servers: [],
      errors: result.error.issues.map(
        (issue) =>
          `COPILOT_MCP_SERVERS${issue.path.length ? `[${issue.path.join(".")}]` : ""}: ${issue.message}`,
      ),
    };
  }
  return { servers: result.data, errors: [] };
}
